import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Briefcase } from "lucide-react";

const field = "rounded-none border border-black bg-background h-10 px-3 focus-visible:ring-0 focus-visible:border-black";
const num = (s) => Number(String(s || "").replace(/[^0-9.]/g, "")) || 0;
const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const FIELDS = [
  ["amount_funded", "Amount Funded (investors send)", "$2,000,000"],
  ["deal_total_return", "Deal Total Payback", "$3,000,000"],
  ["factor_rate", "Factor Rate", "1.50"],
  ["term", "Deal Term", "e.g. 90 days"],
];

export const DealDetails = ({ dealId }) => {
  const [d, setD] = useState({ amount_funded: "", deal_total_return: "", factor_rate: "", term: "", fee_spread: "", holdback_pct: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/site/admin/deals/${dealId}/details`);
      if (data.details) setD((p) => ({ ...p, ...data.details }));
    } catch { /* silent */ }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const funded = num(d.amount_funded);
  const pct = num(d.holdback_pct);
  const fee = Math.round(funded * pct / 100);
  const disbursed = funded - fee;

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/site/admin/deals/${dealId}/details`, { ...d, fee_spread: fee ? usd(fee) : d.fee_spread });
      toast.success("Deal details saved (internal only)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`deal-details-${dealId}`} className="border-t border-black p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Briefcase size={15} />
        <h4 className="mono text-xs uppercase tracking-[0.25em]">Deal Details — Internal Only</h4>
      </div>
      <p className="mono text-[10px] text-muted-foreground">The deal's own economics. Admin-only — never shown to investors and does NOT change the investor payment schedule/returns.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {FIELDS.map(([k, lbl, ph]) => (
          <div key={k}>
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">{lbl}</Label>
            <Input data-testid={`details-${k}`} value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={`${field} mt-1.5`} placeholder={ph} />
          </div>
        ))}
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Holdback Fee %</Label>
          <Input data-testid="details-holdback_pct" value={d.holdback_pct || ""} onChange={(e) => setD({ ...d, holdback_pct: e.target.value })} className={`${field} mt-1.5`} placeholder="10" inputMode="decimal" />
        </div>
      </div>

      {funded > 0 && pct > 0 && (
        <div data-testid="holdback-calc" className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Calc label="Holdback Fee" v={usd(fee)} />
          <Calc label="Cash Disbursed to Business" v={usd(disbursed)} />
          <Calc label="Held in Account (day 1)" v={usd(fee)} big />
        </div>
      )}
      <p className="mono text-[9px] text-muted-foreground">Investors fund {funded ? usd(funded) : "the full amount"}; {pct ? `${pct}% (${usd(fee)})` : "the fee"} is held back as your fee/principal from day one, so {usd(disbursed)} is disbursed to the business. This fee is not split to investors.</p>

      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Notes / Terms</Label>
        <textarea data-testid="details-notes" value={d.notes || ""} onChange={(e) => setD({ ...d, notes: e.target.value })} rows={3}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 focus:outline-none focus:border-black resize-y" placeholder="Any internal notes or terms for this deal." />
      </div>
      <button data-testid="details-save" onClick={save} disabled={saving} className="rounded-none border border-black h-10 px-6 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Deal Details"}</button>
    </div>
  );
};

const Calc = ({ label, v, big }) => (
  <div className="border border-black p-3">
    <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    <div className={`${big ? "text-lg" : "text-base"} font-black tracking-tighter mt-1`}>{v}</div>
  </div>
);
